import React, {
  Fragment,
  useRef,
  useState,
  useContext,
  createContext,
  useEffect,
  useMemo,
} from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import enUS from "date-fns/locale/en-US";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

import {
  Row,
  Col,
  Card,
  Table,
  Modal,
  Badge,
  Dropdown,
  ProgressBar,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
} from "react-table";
import loadingCircleImg from "../../../../images/misc/loading-circle-green.svg";
import landmarkImg from "../../../../images/landmark/logo-text-dark.png";
import ApiService from "../../../../services/ApiService";
import PageHeaderBar from "../../../layouts/PageHeaderBar";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import PersonIcon from "@mui/icons-material/Person";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { styled } from "@mui/material/styles";
import "react-datepicker/dist/react-datepicker.css";
import { utils } from "ethers";
import { MaterialReactTable } from "material-react-table";
import { ExportToCsv } from "export-to-csv";
import { Box, Button } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { FileCopyOutlined as FileCopyOutlinedIcon } from "@mui/icons-material";
import DoneIcon from "@mui/icons-material/Done";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { darken } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { TypeAnimation } from "react-type-animation";
import {
  copyTextToClipboard,
  timeNow,
  useDocumentTitle,
  epochToDateObj,
  epochFromDate,
  uniqueGuid,
} from "../../../utils";
import { ThemeContext } from "../../../../context/ThemeContext";
import {
  epochToDateString,
  epochToDateTimeString,
  stringToRGB,
  toTitleCase,
} from "../../../../utils";
import imageUploadLoader from "../../../../images/misc/loader-light-blue-bg.gif";

const timezoneOptions = ["PT", "ET"];

const OptionForm = ({
  option,
  options,
  employee,
  handleUpdateOption,
  onDraftDirtyChange,
}) => {
  const normalizeOptionLogo = (logo) => {
    if (!logo) {
      return null;
    }
    if (typeof logo === "string") {
      const src = logo.trim();
      if (!src) {
        return null;
      }
      return {
        src,
        scale: 100,
        yOffset: 0,
        xOffset: 0,
        trimEmptySpace: false,
        trimmedSrc: "",
      };
    }
    const src = `${logo.src || logo.url || logo.imageUrl || ""}`.trim();
    if (!src) {
      return null;
    }
    const parsedScale = Number(logo.scale);
    const parsedYOffset = Number(logo.yOffset);
    const parsedXOffset = Number(logo.xOffset);
    return {
      ...logo,
      src,
      scale: Number.isFinite(parsedScale) ? parsedScale : 100,
      yOffset: Number.isFinite(parsedYOffset) ? parsedYOffset : 0,
      xOffset: Number.isFinite(parsedXOffset) ? parsedXOffset : 0,
      trimEmptySpace: !!logo.trimEmptySpace,
      trimmedSrc: `${logo.trimmedSrc || ""}`.trim(),
    };
  };

  const normalizeOptionLogos = (logos = []) => {
    if (!Array.isArray(logos)) {
      return [];
    }
    return logos.map(normalizeOptionLogo).filter(Boolean);
  };

  const isPngLogoSrc = (src = "") => /\.png(?:$|\?)/i.test(`${src}`.trim());

  const toTrimmedPngSrc = (src = "") => {
    const trimmedSrc = `${src}`.trim();
    if (!isPngLogoSrc(trimmedSrc)) {
      return trimmedSrc;
    }
    if (/-trim\.png(?:$|\?)/i.test(trimmedSrc)) {
      return trimmedSrc;
    }
    return trimmedSrc.replace(/\.png(\?.*)?$/i, "-trim.png$1");
  };

  const getLogoPreviewSrc = (logo) => {
    if (!logo || !logo.src) {
      return "";
    }
    if (logo.trimEmptySpace) {
      return logo.trimmedSrc || toTrimmedPngSrc(logo.src);
    }
    return logo.src;
  };

  const [title, setTitle] = useState(option ? option.title : "");
  const [description, setDescription] = useState(
    option ? option.description : ""
  );
  const [location, setLocation] = useState(option ? option.location : "");
  const [startDate, setStartDate] = useState(
    option ? epochToDateObj(option.startTime) : epochToDateObj(timeNow())
  );
  const [endDate, setEndDate] = useState(
    option
      ? epochToDateObj(option.endTime)
      : epochToDateObj(timeNow() + 60 * 90)
  );

  const [shortTitle, setShortTitle] = useState(option ? option.shortTitle : "");
  const [subLocation, setSubLocation] = useState(
    option ? option.subLocation : ""
  );
  const [timeString, setTimeString] = useState(option ? option.timeString : "");
  const [dateString, setDateString] = useState(option ? option.dateString : "");
  const [timezone, setTimezone] = useState(
    option ? option.timezone : timezoneOptions[0]
  );
  const [logos, setLogos] = useState(
    normalizeOptionLogos(option && option.logos ? option.logos : [])
  );
  const [logoTrimLoading, setLogoTrimLoading] = useState({});
  const [activeLogoEditor, setActiveLogoEditor] = useState(null);
  const logoDragStateRef = useRef(null);
  const logoPreviewRefs = useRef({});
  const [controlsPanelPositions, setControlsPanelPositions] = useState({});
  const [hideOnPage, setHideOnPage] = useState(
    option ? !!option.hideOnPage : false
  );
  const [showPresentedByLabel, setShowPresentedByLabel] = useState(
    option ? !!option.showPresentedByLabel : false
  );
  const [startTimeUserChanged, setStartTimeUserChanged] = useState(false);
  const [endTimeUserChanged, setEndTimeUserChanged] = useState(false);

  const [loading, setLoading] = useState(false);

  let allExistingOptionLogos = [];
  if (options && options.length) {
    const allOptionLogos = {};

    options.forEach((o) => {
      const optionLogos = normalizeOptionLogos(o.logos || []); // Default to an empty array if logos is undefined
      optionLogos.forEach((l) => {
        if (l && l.src) {
          allOptionLogos[l.src] = l;
        }
      });
    });

    allExistingOptionLogos = Object.values(allOptionLogos).filter(
      (l) => !logos.some((cl) => cl && cl.src && cl.src === l.src)
    ); // Filter to exclude already added logos
  }

  useEffect(() => {
    registerLocale("en-US", enUS);
  }, []);

  useEffect(() => {
    // new option created
    if (!option || !option.title) {
      setTitle("");
      setDescription("");
      setLocation("");
      setStartDate(epochToDateObj(timeNow()));
      setEndDate(epochToDateObj(timeNow() + 60 * 90));
      setShortTitle("");
      setSubLocation("");
      setTimeString("");
      setDateString("");
      setTimezone(timezoneOptions[0]);
      setLogos([]);
      setLogoTrimLoading({});
      setActiveLogoEditor(null);
      setHideOnPage(false);
      setShowPresentedByLabel(false);
      setStartTimeUserChanged(false);
      setEndTimeUserChanged(false);
    }
    // existing option obj
    else {
      setTitle(option.title);
      setDescription(option.description);
      setLocation(option.location);
      setStartDate(epochToDateObj(option.startTime));
      setEndDate(epochToDateObj(option.endTime));
      setShortTitle(option.shortTitle);
      setSubLocation(option.subLocation);
      setTimeString(option.timeString);
      setDateString(option.dateString);
      setTimezone(option.timezone);
      setLogos(normalizeOptionLogos(option.logos));
      setLogoTrimLoading({});
      setActiveLogoEditor(null);
      setHideOnPage(!!option.hideOnPage);
      setShowPresentedByLabel(!!option.showPresentedByLabel);
      setStartTimeUserChanged(false);
      setEndTimeUserChanged(false);
    }
    console.log(`option`, option);
  }, [option]);

  const normalizeOptionForCompare = (optionObj = {}) => {
    return {
      title: optionObj.title || "",
      description: optionObj.description || "",
      location: optionObj.location || "",
      startTime: optionObj.startTime || null,
      endTime: optionObj.endTime || null,
      shortTitle: optionObj.shortTitle || "",
      subLocation: optionObj.subLocation || "",
      timeString: optionObj.timeString || "",
      dateString: optionObj.dateString || "",
      timezone: optionObj.timezone || timezoneOptions[0],
      logos: normalizeOptionLogos(optionObj.logos || []),
      hideOnPage: !!optionObj.hideOnPage,
      showPresentedByLabel: !!optionObj.showPresentedByLabel,
    };
  };

  const optionIsDirty = useMemo(() => {
    const baselineOption = normalizeOptionForCompare(option || {});
    const currentOption = normalizeOptionForCompare({
      title,
      description,
      location,
      startTime: epochFromDate(startDate),
      endTime: epochFromDate(endDate),
      shortTitle,
      subLocation,
      timeString,
      dateString,
      timezone,
      logos: normalizeOptionLogos(logos),
      hideOnPage,
      showPresentedByLabel,
    });
    return JSON.stringify(baselineOption) !== JSON.stringify(currentOption);
  }, [
    option,
    title,
    description,
    location,
    startDate,
    endDate,
    shortTitle,
    subLocation,
    timeString,
    dateString,
    timezone,
    logos,
    hideOnPage,
    showPresentedByLabel,
  ]);

  useEffect(() => {
    if (onDraftDirtyChange) {
      onDraftDirtyChange(optionIsDirty);
    }
  }, [optionIsDirty, onDraftDirtyChange]);

  const handleSetStartDate = (date) => {
    console.log(`handleSetStartDate() - date`, date);
    setStartDate(date);
    setStartTimeUserChanged(true);
  };

  const handleSetEndDate = (date) => {
    console.log(`handleSetStartDate() - date`, date);
    setEndDate(date);
    setEndTimeUserChanged(true);
  };

  const isValidDate = (dateObj) => dateObj instanceof Date && !isNaN(dateObj);

  const formatDisplayDate = (dateObj) =>
    dateObj.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  const formatTimePart = (timeDate, includeMeridiem = true) => {
    const hours24 = timeDate.getHours();
    const minutes = `${timeDate.getMinutes()}`.padStart(2, "0");
    const hours12 = hours24 % 12 || 12;
    const meridiem = hours24 >= 12 ? "pm" : "am";
    return includeMeridiem
      ? `${hours12}:${minutes}${meridiem}`
      : `${hours12}:${minutes}`;
  };

  useEffect(() => {
    if (!startTimeUserChanged || dateString.trim() || !isValidDate(startDate)) {
      return;
    }
    setDateString(formatDisplayDate(startDate));
  }, [startDate, startTimeUserChanged, dateString]);

  useEffect(() => {
    if (
      !startTimeUserChanged ||
      !endTimeUserChanged ||
      timeString.trim() ||
      !isValidDate(startDate) ||
      !isValidDate(endDate)
    ) {
      return;
    }
    setTimeString(
      `${formatTimePart(startDate, false)} - ${formatTimePart(endDate, true)} ${
        timezone || "PT"
      }`
    );
  }, [
    startDate,
    endDate,
    startTimeUserChanged,
    endTimeUserChanged,
    timeString,
    timezone,
  ]);

  const handleSaveOption = () => {
    const updatedOption = {
      title,
      description,
      location,
      startTime: epochFromDate(startDate),
      endTime: epochFromDate(endDate),
      shortTitle,
      subLocation,
      timeString,
      dateString,
      timezone,
      logos,
      hideOnPage,
      showPresentedByLabel,
    };
    if (option && (option.optionId || option.tempOptionId)) {
      // set optionId so handleUpdateOption knows we are editing not creating
      updatedOption.optionId = option.optionId;
      updatedOption.tempOptionId = option.tempOptionId;
    }
    console.log(`handleUpdateOption() - option`, option);
    console.log(`handleUpdateOption() - updatedOption`, updatedOption);
    handleUpdateOption(updatedOption);
    if (onDraftDirtyChange) {
      onDraftDirtyChange(false);
    }
  };

  const handleDiscardNewOption = () => {
    if (
      optionIsDirty &&
      !window.confirm("Discard this new option and lose all unsaved changes?")
    ) {
      return;
    }
    if (onDraftDirtyChange) {
      onDraftDirtyChange(false);
    }
    handleUpdateOption({ discardNewOption: true });
  };

  // Helper functions for dynamic arrays
  const addArrayItem = (setter, array, newItem) => {
    setter([...array, newItem]);
  };

  const removeArrayItem = (setter, array, index) => {
    const updatedArray = array.filter((_, i) => i !== index);
    setter(updatedArray);
  };

  // Handle image upload
  const handleImageUpload = async (event, setter, options = {}) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true); // Start loading

    const formData = new FormData();
    formData.append("image", file);
    formData.append("options", JSON.stringify(options));

    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/upload/image", formData);
      if (response.success && response.imageUrl) {
        setter((prev) => [
            ...prev,
            {
              src: response.imageUrl,
              scale: 100,
              yOffset: 0,
              xOffset: 0,
              trimEmptySpace: false,
              trimmedSrc: "",
            },
          ]);
        toast.success("Image uploaded successfully");
      } else {
        toast.error("Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error uploading image");
    } finally {
      setLoading(false); // End loading
    }
  };

  const newOptionMode =
    !option || (!option.optionId && !option.tempOptionId) ? true : false; // user is creating a new option or editing existing

  const isDateValid = /^[A-Za-z]+,\s[A-Za-z]+\s\d{1,2},\s\d{4}$/.test(
    dateString
  );
  const isTimeValid =
    /^\d{1,2}:\d{2}(am|pm)?\s-\s\d{1,2}:\d{2}(am|pm)\s[A-Z]{2,4}$/i.test(
      timeString
    );

  const scaleOptionLogoMaxWidth = (scale) => {
    const maxWidth = 162; //.ed-wrapper .header-bar .header-container .header-logos .header-logo
    if (scale === 100) {
      return maxWidth;
    } else {
      // Calculate the width based on the scale and round to the nearest pixel
      return Math.round((scale / 100) * maxWidth);
    }
  };

  const clampLogoFieldValue = (field, rawValue) => {
    const numericValue = parseInt(rawValue, 10);
    const fallbackValue = field === "scale" ? 100 : 0;
    if (!Number.isFinite(numericValue)) {
      return fallbackValue;
    }
    if (field === "scale") {
      return Math.max(1, Math.min(200, numericValue));
    }
    return numericValue;
  };

  const CONTROLS_PANEL_LEFT_OFFSET = 240;
  const CONTROLS_PANEL_WIDTH = 200;

  const updateControlsPanelPositions = () => {
    const nextPositions = {};
    logos.forEach((_, index) => {
      const previewEl = logoPreviewRefs.current[index];
      if (!previewEl) {
        return;
      }
      const rect = previewEl.getBoundingClientRect();
      nextPositions[index] = {
        top: Math.round(rect.top),
        left: Math.round(rect.left - CONTROLS_PANEL_LEFT_OFFSET),
      };
    });
    setControlsPanelPositions(nextPositions);
  };

  const getControlsPanelStyle = (index) => {
    const pos = controlsPanelPositions[index];
    if (!pos) {
      return {
        position: "fixed",
        visibility: "hidden",
        width: `${CONTROLS_PANEL_WIDTH}px`,
        right: "auto",
        transform: "none",
      };
    }
    return {
      position: "fixed",
      top: `${pos.top}px`,
      left: `${pos.left}px`,
      width: `${CONTROLS_PANEL_WIDTH}px`,
      zIndex: 2000,
      right: "auto",
      transform: "none",
      overflow: "visible",
    };
  };

  const updateLogoFieldValue = (logoIndex, field, rawValue) => {
    const clampedValue = clampLogoFieldValue(field, rawValue);
    setLogos((prevLogos) =>
      prevLogos.map((logoItem, idx) =>
        idx === logoIndex ? { ...logoItem, [field]: clampedValue } : logoItem
      )
    );
  };

  const beginLogoFieldEdit = (logoIndex, field) => {
    const logoItem = logos[logoIndex];
    if (!logoItem) {
      return;
    }
    const fieldValue =
      field === "scale" ? logoItem.scale || 100 : logoItem.yOffset || 0;
    setActiveLogoEditor({
      logoIndex,
      field,
      value: `${fieldValue}`,
    });
  };

  const commitLogoFieldEdit = () => {
    if (!activeLogoEditor) {
      return;
    }
    const { logoIndex, field, value } = activeLogoEditor;
    updateLogoFieldValue(logoIndex, field, value);
    setActiveLogoEditor(null);
  };

  const startLogoFieldDrag = (logoIndex, field, event) => {
    if (activeLogoEditor) {
      return;
    }
    const logoItem = logos[logoIndex];
    if (!logoItem) {
      return;
    }
    event.preventDefault();
    const startValue = field === "scale" ? logoItem.scale || 100 : logoItem.yOffset || 0;
    logoDragStateRef.current = {
      logoIndex,
      field,
      startY: event.clientY,
      startValue,
      lastStep: 0,
    };
    document.body.style.cursor = "ns-resize";
  };

  useEffect(() => {
    const handleWindowMouseMove = (event) => {
      const dragState = logoDragStateRef.current;
      if (!dragState) {
        return;
      }
      const deltaY = dragState.startY - event.clientY;
      const step = Math.trunc(deltaY); // 1px drag = 1 unit change
      if (step === dragState.lastStep) {
        return;
      }
      dragState.lastStep = step;
      const nextValue = dragState.startValue + step;
      updateLogoFieldValue(dragState.logoIndex, dragState.field, nextValue);
    };

    const handleWindowMouseUp = () => {
      if (!logoDragStateRef.current) {
        return;
      }
      logoDragStateRef.current = null;
      document.body.style.cursor = "";
    };

    window.addEventListener("mousemove", handleWindowMouseMove);
    window.addEventListener("mouseup", handleWindowMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleWindowMouseMove);
      window.removeEventListener("mouseup", handleWindowMouseUp);
      document.body.style.cursor = "";
    };
  }, []);

  useEffect(() => {
    const syncPositions = () => {
      updateControlsPanelPositions();
    };
    syncPositions();
    window.addEventListener("resize", syncPositions);
    window.addEventListener("scroll", syncPositions, true);
    return () => {
      window.removeEventListener("resize", syncPositions);
      window.removeEventListener("scroll", syncPositions, true);
    };
  }, [logos]);

  const handleToggleTrimEmptySpace = async (index, checked) => {
    const targetLogo = logos[index];
    if (!targetLogo) {
      return;
    }

    if (!checked) {
      setLogos((prevLogos) =>
        prevLogos.map((logo, logoIndex) =>
          logoIndex === index ? { ...logo, trimEmptySpace: false } : logo
        )
      );
      return;
    }

    if (!isPngLogoSrc(targetLogo.src)) {
      toast.error("Trim Empty Space is only supported for PNG logos");
      return;
    }

    if (targetLogo.trimmedSrc) {
      setLogos((prevLogos) =>
        prevLogos.map((logo, logoIndex) =>
          logoIndex === index ? { ...logo, trimEmptySpace: true } : logo
        )
      );
      return;
    }

    setLogoTrimLoading((prev) => ({ ...prev, [index]: true }));
    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/upload/trimPngWhitespace", {
        imageUrl: targetLogo.src,
      });
      if (!response?.success || !response?.imageUrl) {
        throw new Error(response?.error || "No trimmed image URL returned");
      }
      setLogos((prevLogos) =>
        prevLogos.map((logo, logoIndex) =>
          logoIndex === index
            ? {
                ...logo,
                trimEmptySpace: true,
                trimmedSrc: response.imageUrl,
              }
            : logo
        )
      );
      toast.success("Trimmed logo is ready");
    } catch (error) {
      console.error("Error trimming logo whitespace:", error);
      toast.error("Failed to trim logo whitespace");
      setLogos((prevLogos) =>
        prevLogos.map((logo, logoIndex) =>
          logoIndex === index ? { ...logo, trimEmptySpace: false } : logo
        )
      );
    } finally {
      setLogoTrimLoading((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  return (
    <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
      <div className="option-form">
        <div className="row">
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">
              Title{" "}
              <span className="example-text">(e.g. Welcome to CES 2024)</span>
            </label>
            <input
              type="text"
              value={title}
              className="form-control"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">
              Small Title{" "}
              <span className="example-text">(e.g. Happy Hour)</span>
            </label>
            <input
              type="text"
              value={shortTitle}
              className="form-control"
              onChange={(e) => setShortTitle(e.target.value)}
            />
          </div>
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">
              Location{" "}
              <span className="example-text">(e.g. The Palazzo Hotel)</span>
            </label>
            <input
              type="text"
              value={location}
              className="form-control"
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">
              Sub Location{" "}
              <span className="example-text">(e.g. Rooftop Bar)</span>
            </label>
            <input
              type="text"
              value={subLocation}
              className="form-control"
              onChange={(e) => setSubLocation(e.target.value)}
            />
          </div>
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">
              Description{" "}
              <span className="example-text">
                (e.g. Delicious vegan brunch and networking meetup for VIPs)
              </span>
            </label>
            <textarea
              type="text"
              rows="3"
              value={description}
              className="form-control"
              onChange={(e) => setDescription(e.target.value)}
              style={{ lineHeight: "1.4", height: "90px" }}
            />
          </div>
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">
              Timezone{" "}
              <span className="example-text">
                (e.g. {timezoneOptions[0]}) (contact mjones if you need a
                different timezone)
              </span>
            </label>
            <select
              defaultValue={timezoneOptions[0]}
              className="form-control"
              onChange={(e) => setTimezone(e.target.value)}
              value={timezone}
            >
              {timezoneOptions.map((tz) => {
                return <option>{tz}</option>;
              })}
            </select>
          </div>
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">
              Start Time <br />
              <span className="example-text">(e.g. 01/10/2024, 12:00 AM)</span>
            </label>
            <DatePicker
              selected={startDate}
              onChange={(date) => handleSetStartDate(date)}
              showTimeSelect
              locale="en-US"
              // showTimeInput
              timeIntervals={15}
              dateFormat="Pp"
              className="form-control"
            />
          </div>
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">
              End Time <br />
              <span className="example-text">(e.g. 01/10/2024, 2:00 AM)</span>
            </label>
            <DatePicker
              selected={endDate}
              onChange={(date) => handleSetEndDate(date)}
              locale="en-US"
              showTimeSelect
              // showTimeInput
              timeIntervals={15}
              dateFormat="Pp"
              className="form-control"
            />
          </div>
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">
              Display Date <br />
              <span className="example-text">
                (e.g. Tuesday, January 9, 2024)
              </span>
            </label>
            <input
              type="text"
              value={dateString}
              className="form-control"
              onChange={(e) => setDateString(e.target.value)}
            />
            {!isDateValid && dateString && (
              <small className="text-danger">
                Please use the format: Tuesday, January 9, 2024
              </small>
            )}
          </div>
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">
              Display Time <br />
              <span className="example-text">(e.g. 4:00 - 6:00pm {timezone ? timezone : "PT"})</span>
            </label>
            <input
              type="text"
              value={timeString}
              className="form-control"
              onChange={(e) => setTimeString(e.target.value)}
            />
            {!isTimeValid && timeString && (
              <small className="text-danger">
                Please use the format: 4:00 - 6:00pm {timezone ? timezone : "PT"}
              </small>
            )}
          </div>
          <div className="form-group mb-3 col-md-12">
            <div className="form-check option-hide-toggle">
              <input
                id="hideOptionOnPage"
                type="checkbox"
                className="form-check-input option-hide-checkbox"
                checked={hideOnPage}
                onChange={(e) => setHideOnPage(e.target.checked)}
              />
              <label className="form-check-label option-hide-label" htmlFor="hideOptionOnPage">
                Hide option on page
              </label>
            </div>
          </div>
          <div className="form-group mb-3 col-md-12">
            <div className="form-check option-hide-toggle">
              <input
                id="showPresentedByLabel"
                type="checkbox"
                className="form-check-input option-hide-checkbox"
                checked={showPresentedByLabel}
                onChange={(e) => setShowPresentedByLabel(e.target.checked)}
              />
              <label className="form-check-label option-hide-label" htmlFor="showPresentedByLabel">
                Show "PRESENTED BY" label above logos
              </label>
            </div>
          </div>
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">Logo Images</label>
            <div className="existing-logos-container">
              <label className="form-label">
                Click to add a previously used logo:
              </label>
              <div className="existing-logos-outer">
                {allExistingOptionLogos.map((hl, index) => {
                  return (
                    <div
                      onClick={() => {
                        const normalizedLogo = normalizeOptionLogo(hl);
                        if (!normalizedLogo) {
                          return;
                        }
                        setLogos([...logos, normalizedLogo]); // add the new logo object
                      }}
                      key={index}
                      className="existing-logo"
                      style={{
                        maxWidth: `${scaleOptionLogoMaxWidth(hl.scale || 100)}px`,
                      }}
                    >
                      <img
                        src={getLogoPreviewSrc(hl) || hl.src}
                        alt={`Header Logo ${index + 1}`}
                        className="img-fluid"
                         style={{
                          maxWidth: `${scaleOptionLogoMaxWidth(hl.scale || 100)}px`,
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            {logos.map((logo, index) => {
              const previewSrc = getLogoPreviewSrc(logo);
              const logoStyle = {
                maxWidth: `${scaleOptionLogoMaxWidth(logo.scale || 100)}px`,
              };
              if (logo.yOffset && logo.yOffset !== 0) {
                logoStyle.transform = `translateY(${logo.yOffset}px)`;
              }
              return (
                <div
                  key={index}
                  className="d-flex align-items-center mb-2 option-logo-row"
                  style={{ marginTop: `${index === 0 ? "0" : "16px"}` }}
                >
                  <div
                    className="header-logo-container img-thumbnail"
                    ref={(el) => {
                      if (el) {
                        logoPreviewRefs.current[index] = el;
                      } else {
                        delete logoPreviewRefs.current[index];
                      }
                    }}
                  >
                    <img
                      src={previewSrc || logo.src}
                      alt={`Logo ${index + 1}`}
                      className="img-fluid"
                      style={logoStyle}
                    />
                  </div>
                  {/* Input to adjust the scale and yOffset of the image */}
                  <div
                    className="controls-container"
                    style={getControlsPanelStyle(index)}
                  >
                    <div className="logo-control-row">
                      <label className="form-label mb-0">Scale %</label>
                      {activeLogoEditor &&
                      activeLogoEditor.logoIndex === index &&
                      activeLogoEditor.field === "scale" ? (
                        <input
                          type="number"
                          className="form-control inline-number-input"
                          value={activeLogoEditor.value}
                          autoFocus
                          min="1"
                          max="200"
                          onChange={(event) =>
                            setActiveLogoEditor((prevEditor) => ({
                              ...prevEditor,
                              value: event.target.value,
                            }))
                          }
                          onBlur={commitLogoFieldEdit}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              commitLogoFieldEdit();
                            } else if (event.key === "Escape") {
                              setActiveLogoEditor(null);
                            }
                          }}
                        />
                      ) : (
                        <div
                          className="draggable-number-value"
                          onMouseDown={(event) =>
                            startLogoFieldDrag(index, "scale", event)
                          }
                          onDoubleClick={() => beginLogoFieldEdit(index, "scale")}
                          title="Drag up/down to adjust, or double-click to type"
                        >
                          {logo.scale || 100}
                        </div>
                      )}
                    </div>

                    <div className="logo-control-row">
                      <label className="form-label mb-0">Y Offset (px)</label>
                      {activeLogoEditor &&
                      activeLogoEditor.logoIndex === index &&
                      activeLogoEditor.field === "yOffset" ? (
                        <input
                          type="number"
                          className="form-control inline-number-input"
                          value={activeLogoEditor.value}
                          autoFocus
                          onChange={(event) =>
                            setActiveLogoEditor((prevEditor) => ({
                              ...prevEditor,
                              value: event.target.value,
                            }))
                          }
                          onBlur={commitLogoFieldEdit}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              commitLogoFieldEdit();
                            } else if (event.key === "Escape") {
                              setActiveLogoEditor(null);
                            }
                          }}
                        />
                      ) : (
                        <div
                          className="draggable-number-value"
                          onMouseDown={(event) =>
                            startLogoFieldDrag(index, "yOffset", event)
                          }
                          onDoubleClick={() => beginLogoFieldEdit(index, "yOffset")}
                          title="Drag up/down to adjust, or double-click to type"
                        >
                          {logo.yOffset || 0}
                        </div>
                      )}
                    </div>

                    {isPngLogoSrc(logo.src) ? (
                      <div className="trim-empty-space-container">
                        <div className="trim-empty-space-input">
                          <label
                            htmlFor={`trim-empty-space-${index}`}
                            className="trim-empty-space-inline"
                          >
                          <input
                            id={`trim-empty-space-${index}`}
                            type="checkbox"
                            checked={!!logo.trimEmptySpace}
                            onChange={(e) =>
                              handleToggleTrimEmptySpace(index, e.target.checked)
                            }
                            disabled={!!logoTrimLoading[index]}
                          />
                            <span>Trim Empty Space</span>
                          </label>
                          {logoTrimLoading[index] ? (
                            <span className="ms-2 d-flex align-items-center">
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              <span className="ms-2">Trimming...</span>
                            </span>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="remove-logo-container">
                    <button
                      className="btn btn-danger ml-2"
                      onClick={() =>
                        removeArrayItem(setLogos, logos, index)
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
// (
//               <div key={index} className="d-flex align-items-center mb-2">
//                 <img
//                   src={logo.src}
//                   alt={`Logo ${index + 1}`}
//                   className="img-thumbnail"
//                   style={{ maxWidth: "344px", width: "344px" }}
//                 />
//                 <button
//                   className="btn btn-danger ml-2"
//                   onClick={() => removeArrayItem(setLogos, logos, index)}
//                 >
//                   Remove
//                 </button>
//               </div>
//             )
            }
)}
            {loading && (
              <img
                src={imageUploadLoader}
                alt="Uploading..."
                width="100"
                className="img-thumbnail"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                handleImageUpload(e, setLogos, {
                  compress: true,
                })
              }
              className="d-none"
              id="subEventsLogoUpload"
            />
            <button
              className="btn btn-secondary btn-sm mt-2"
              onClick={() =>
                document.getElementById("subEventsLogoUpload").click()
              }
            >
              Upload Logo
              <i className="event-owner-add fa-sharp fa-solid fa-plus ml-05"></i>
            </button>
          </div>
          <div className="divider"></div>
          <div className="form-group mb-3 col-md-12">
            {newOptionMode && (
              <div className="option-new-actions">
                <button
                  className={`create-event-btn option-apply-btn btn btn-sm btn-outline-primary`}
                  onClick={handleSaveOption}
                >
                  {`Add Option to Event`}
                  <i className="event-owner-add fa-sharp fa-solid fa-check ml-05"></i>
                </button>
                <button
                  className={`create-event-btn option-discard-btn btn btn-sm btn-outline-secondary`}
                  onClick={handleDiscardNewOption}
                >
                  {`Discard Option`}
                  <i className="event-owner-add fa-sharp fa-solid fa-xmark ml-05"></i>
                </button>
              </div>
            )}
            {!newOptionMode && (
              <>
                {optionIsDirty ? (
                  <button
                    className={`create-event-btn option-apply-btn btn btn-sm btn-outline-primary`}
                    onClick={handleSaveOption}
                  >
                    {`Apply Option Changes`}
                    <i className="event-owner-add fa-sharp fa-solid fa-check ml-05"></i>
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Full-width single-line-looking textarea that grows to fit its content.
const AutoGrowTextarea = ({ value, onChange }) => {
  const textareaRef = useRef(null);
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={textareaRef}
      rows={1}
      value={value}
      onChange={onChange}
      className="form-control"
      style={{ overflow: "hidden", resize: "none" }}
    />
  );
};

// BD portfolio page copy stored under event.bdOptions.
// bdIntroParagraphs is edited as text (one paragraph per line) and saved as an array.
const bdOptionsFromEvent = (eventObj) => {
  const bd = eventObj && eventObj.bdOptions ? eventObj.bdOptions : {};
  return {
    bdHeroTitle: bd.bdHeroTitle || "",
    bdHeroSubheading: bd.bdHeroSubheading || "",
    bdHeroBody: bd.bdHeroBody || "",
    bdHeroButtonText: bd.bdHeroButtonText || "",
    bdIntroParagraphsText: Array.isArray(bd.bdIntroParagraphs)
      ? bd.bdIntroParagraphs.join("\n\n")
      : "",
    bdRequestTitle: bd.bdRequestTitle || "",
    bdRequestSubtitle: bd.bdRequestSubtitle || "",
    bdContactName: bd.bdContactName || "",
    bdContactEmail: bd.bdContactEmail || "",
    bdSuccessMessage: bd.bdSuccessMessage || "",
    bdMetaTitle: bd.bdMetaTitle || "",
    bdMetaDescription: bd.bdMetaDescription || "",
    bdMetaUrl: bd.bdMetaUrl || "",
    bdMetaImageUrl: bd.bdMetaImageUrl || "",
  };
};

const EventForm = ({
  selectedEvent,
  employee,
  getEvents,
  resetModes,
  events,
  onEventsUpdated,
  eventsTag = "global",
}) => {
  const isBdMode = eventsTag === "bdportfolio";
  const normalizeStringArray = (values = []) => {
    if (typeof values === "string") {
      values = values.split(",");
    }
    if (!Array.isArray(values)) {
      return [];
    }
    return [...new Set(values.map((value) => `${value || ""}`.trim()).filter(Boolean))];
  };

  const [title, setTitle] = useState(selectedEvent ? selectedEvent.title : "");
  const [startDate, setStartDate] = useState(
    selectedEvent ? selectedEvent.startDate : ""
  );
  const [endDate, setEndDate] = useState(
    selectedEvent ? selectedEvent.endDate : ""
  );
  const [location, setLocation] = useState(
    selectedEvent ? selectedEvent.location : ""
  );
  const [imageUrl, setImageUrl] = useState(
    selectedEvent ? selectedEvent.imageUrl : ""
  );
  const [signupEmailsEnabled, setSignupEmailsEnabled] = useState(
    selectedEvent ? !!selectedEvent.signupEmailsEnabled : false
  );
  const [tags, setTags] = useState(
    selectedEvent ? selectedEvent.tags : [eventsTag]
  );
  const [bdOptions, setBdOptions] = useState(bdOptionsFromEvent(selectedEvent));
  const [options, setOptions] = useState(
    selectedEvent ? selectedEvent.options : []
  );
  const [companyCategories, setCompanyCategories] = useState(
    selectedEvent ? normalizeStringArray(selectedEvent.companyCategories || []) : []
  );
  const [companyCategoryDraft, setCompanyCategoryDraft] = useState("");
  const [areasOfInterest, setAreasOfInterest] = useState(
    selectedEvent ? normalizeStringArray(selectedEvent.areasOfInterest || []) : []
  );
  const [areaOfInterestDraft, setAreaOfInterestDraft] = useState("");
  const [companyOptions, setCompanyOptions] = useState(
    selectedEvent
      ? (selectedEvent.companyOptions || []).map((companyOptionObj) => ({
          companyId: companyOptionObj.companyId || "",
          companyName: companyOptionObj.companyName || "",
          companyCategory: `${companyOptionObj.companyCategory || ""}`.trim(),
          companyTags: normalizeStringArray(companyOptionObj.companyTags || []),
          companyWebsite: companyOptionObj.companyWebsite || "",
          companySummary: companyOptionObj.companySummary || "",
          companyDescription: companyOptionObj.companyDescription || "",
          companyLogoUrl: companyOptionObj.companyLogoUrl || "",
        }))
      : []
  );
  const [companyTagDrafts, setCompanyTagDrafts] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [optionDraftDirty, setOptionDraftDirty] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [activeCompanyRowIndex, setActiveCompanyRowIndex] = useState(null);
  const [dragOverCompanyRowIndex, setDragOverCompanyRowIndex] = useState(null);
  const optionItemRefs = useRef({});

  const [loading, setLoading] = useState(false);
  const normalizeCompanyOptionForCompare = (companyOptionObj = {}) => {
    return {
      companyId: companyOptionObj.companyId || "",
      companyName: companyOptionObj.companyName || "",
      companyCategory: `${companyOptionObj.companyCategory || ""}`.trim(),
      companyTags: normalizeStringArray(companyOptionObj.companyTags || []),
      companyWebsite: companyOptionObj.companyWebsite || "",
      companySummary: companyOptionObj.companySummary || "",
      companyDescription: companyOptionObj.companyDescription || "",
      companyLogoUrl: companyOptionObj.companyLogoUrl || "",
    };
  };

  const createNewCompanyOption = () => ({
    companyId: uniqueGuid().slice(0, 6),
    companyName: "",
    companyCategory: "",
    companyTags: [],
    companyWebsite: "",
    companySummary: "",
    companyDescription: "",
    companyLogoUrl: "",
  });

  const allExistingCompanyOptions = useMemo(() => {
    if (!events || !events.length) {
      return [];
    }
    const companyBySignature = {};
    events.forEach((eventObj) => {
      (eventObj.companyOptions || []).forEach((companyOptionObj) => {
        const normalizedCompany = normalizeCompanyOptionForCompare(companyOptionObj);
        const signature = [
          normalizedCompany.companyName.trim().toLowerCase(),
          normalizedCompany.companyCategory.trim().toLowerCase(),
          normalizedCompany.companyTags.join(",").toLowerCase(),
          normalizedCompany.companyWebsite.trim().toLowerCase(),
          normalizedCompany.companySummary.trim().toLowerCase(),
          normalizedCompany.companyDescription.trim().toLowerCase(),
          normalizedCompany.companyLogoUrl.trim(),
        ].join("|");
        if (!signature.replace(/\|/g, "").length) {
          return;
        }
        companyBySignature[signature] = {
          ...normalizedCompany,
          companyId: normalizedCompany.companyId || uniqueGuid().slice(0, 6),
        };
      });
    });
    const selectedSignatures = new Set(
      companyOptions.map((companyOptionObj) => {
        const normalizedCompany = normalizeCompanyOptionForCompare(companyOptionObj);
        return [
          normalizedCompany.companyName.trim().toLowerCase(),
          normalizedCompany.companyCategory.trim().toLowerCase(),
          normalizedCompany.companyTags.join(",").toLowerCase(),
          normalizedCompany.companyWebsite.trim().toLowerCase(),
          normalizedCompany.companySummary.trim().toLowerCase(),
          normalizedCompany.companyDescription.trim().toLowerCase(),
          normalizedCompany.companyLogoUrl.trim(),
        ].join("|");
      })
    );
    return Object.values(companyBySignature).filter((companyOptionObj) => {
      const signature = [
        companyOptionObj.companyName.trim().toLowerCase(),
        companyOptionObj.companyCategory.trim().toLowerCase(),
        companyOptionObj.companyTags.join(",").toLowerCase(),
        companyOptionObj.companyWebsite.trim().toLowerCase(),
        companyOptionObj.companySummary.trim().toLowerCase(),
        companyOptionObj.companyDescription.trim().toLowerCase(),
        companyOptionObj.companyLogoUrl.trim(),
      ].join("|");
      return !selectedSignatures.has(signature);
    });
  }, [events, companyOptions]);

  const allExistingEventLogos = useMemo(() => {
    if (!events || !events.length) {
      return [];
    }
    const logosBySrc = {};
    const otherEvents =
      selectedEvent && selectedEvent.eventId
        ? events.filter((eventObj) => eventObj.eventId !== selectedEvent.eventId)
        : events;
    otherEvents.forEach((eventObj) => {
      const logoSrc = (eventObj.imageUrl || "").trim();
      if (logoSrc.length) {
        logosBySrc[logoSrc] = logoSrc;
      }
    });
    return Object.values(logosBySrc).filter((logoSrc) => logoSrc !== imageUrl);
  }, [events, selectedEvent, imageUrl]);


  async function addOrEditEvent(event, editMode = false) {
    try {
      const api = new ApiService({ employee });
      const url = editMode
        ? "landmark/events/editEvent"
        : "landmark/events/addEvent";
      const eventsResponse = await api.post(url, {
        event,
      });
      console.log(`addOrEditEvent() - ${url} - response`, eventsResponse);
      const { success, error } = eventsResponse;
      if (!success) {
        toast.error("An error occurred");
      } else if (error) {
        toast.error(error);
      } else {
        toast.success(success);
        // refresh events
        getEvents();
        if (onEventsUpdated) {
          onEventsUpdated();
        }
        resetModes(); // exit the edit/create form
      }
    } catch (error) {
      console.error(error);
    }
  }
  // Handle image upload
  const handleImageUpload = async (event, setter, options = {}) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true); // Start loading

    const formData = new FormData();
    formData.append("image", file);
    formData.append("options", JSON.stringify(options));

    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/upload/image", formData);
      if (response.success && response.imageUrl) {
        setter(response.imageUrl);
        toast.success("Image uploaded successfully");
      } else {
        toast.error("Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error uploading image");
    } finally {
      setLoading(false); // End loading
    }
  };

  const updateBdOption = (field) => (e) => {
    const value = e.target.value;
    setBdOptions((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEvent = () => {
    if (optionDraftDirty) {
      toast.error("Apply option changes before saving the event");
      return;
    }
    const normalizedCompanyCategories = normalizeStringArray(companyCategories);
    // scrape details from state
    const event = {
      title,
      location,
      startDate,
      endDate,
      imageUrl,
      signupEmailsEnabled,
      tags,
      options,
      areasOfInterest: normalizeStringArray(areasOfInterest),
      companyCategories: normalizedCompanyCategories,
      companyOptions: companyOptions.map((companyOptionObj) => {
        const companyCategory = `${companyOptionObj.companyCategory || ""}`.trim();
        return {
          companyId: companyOptionObj.companyId || uniqueGuid().slice(0, 6),
          companyName: (companyOptionObj.companyName || "").trim(),
          companyCategory: normalizedCompanyCategories.includes(companyCategory)
            ? companyCategory
            : "",
          companyTags: normalizeStringArray(companyOptionObj.companyTags || []),
          companyWebsite: (companyOptionObj.companyWebsite || "").trim(),
          companySummary: (companyOptionObj.companySummary || "").trim(),
          companyDescription: (companyOptionObj.companyDescription || "").trim(),
          companyLogoUrl: (companyOptionObj.companyLogoUrl || "").trim(),
        };
      }),
    };
    if (isBdMode) {
      event.bdOptions = {
        bdHeroTitle: (bdOptions.bdHeroTitle || "").trim(),
        bdHeroSubheading: (bdOptions.bdHeroSubheading || "").trim(),
        bdHeroBody: (bdOptions.bdHeroBody || "").trim(),
        bdHeroButtonText: (bdOptions.bdHeroButtonText || "").trim(),
        bdIntroParagraphs: (bdOptions.bdIntroParagraphsText || "")
          .split(/\n+/)
          .map((paragraph) => paragraph.trim())
          .filter(Boolean),
        bdRequestTitle: (bdOptions.bdRequestTitle || "").trim(),
        bdRequestSubtitle: (bdOptions.bdRequestSubtitle || "").trim(),
        bdContactName: (bdOptions.bdContactName || "").trim(),
        bdContactEmail: (bdOptions.bdContactEmail || "").trim(),
        bdSuccessMessage: (bdOptions.bdSuccessMessage || "").trim(),
        bdMetaTitle: (bdOptions.bdMetaTitle || "").trim(),
        bdMetaDescription: (bdOptions.bdMetaDescription || "").trim(),
        bdMetaUrl: (bdOptions.bdMetaUrl || "").trim(),
        bdMetaImageUrl: (bdOptions.bdMetaImageUrl || "").trim(),
      };
    }
    // saving an existing event - use edit endpoint
    if (selectedEvent && selectedEvent.eventId) {
      event.eventId = selectedEvent.eventId;
      // edit event
      addOrEditEvent(event, true);
    } else {
      // add event
      addOrEditEvent(event, false);
    }
  };

  useEffect(() => {
    // new event created
    if (!selectedEvent || !selectedEvent.eventId) {
      setTitle("");
      setStartDate("");
      setEndDate("");
      setLocation("");
      setImageUrl("");
      setSignupEmailsEnabled(false);
      setTags([eventsTag]);
      setBdOptions(bdOptionsFromEvent(null));
      setOptions([]);
      setAreasOfInterest([]);
      setAreaOfInterestDraft("");
      setCompanyCategories([]);
      setCompanyCategoryDraft("");
      setCompanyOptions([]);
      setCompanyTagDrafts({});
      setSelectedOption(null);
      setOptionDraftDirty(false);
    }
    // existing event obj
    else {
      setTitle(selectedEvent.title);
      setStartDate(selectedEvent.startDate);
      setEndDate(selectedEvent.endDate);
      setLocation(selectedEvent.location);
      setImageUrl(selectedEvent.imageUrl);
      setSignupEmailsEnabled(!!selectedEvent.signupEmailsEnabled);
      setTags(selectedEvent.tags);
      setBdOptions(bdOptionsFromEvent(selectedEvent));
      setOptions(selectedEvent.options);
      setAreasOfInterest(normalizeStringArray(selectedEvent.areasOfInterest || []));
      setAreaOfInterestDraft("");
      setCompanyCategories(normalizeStringArray(selectedEvent.companyCategories || []));
      setCompanyCategoryDraft("");
      setCompanyOptions(
        (selectedEvent.companyOptions || []).map(normalizeCompanyOptionForCompare)
      );
      setCompanyTagDrafts({});
      setSelectedOption(null);
      setOptionDraftDirty(false);
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (selectedOption === null) {
      setOptionDraftDirty(false);
    }
  }, [selectedOption]);

  // called when user has saved an option
  const handleUpdateOption = (option) => {
    if (option && option.discardNewOption) {
      setSelectedOption(null);
      setOptionDraftDirty(false);
      toast.success("New Option Discarded");
      return;
    }
    let appliedOptionKey = getOptionKey(option);
    // option already exists
    if (option.optionId || option.tempOptionId) {
      const updatedOptions = options.map((o) => {
        if (
          (option.optionId && o.optionId && option.optionId === o.optionId) ||
          (option.tempOptionId &&
            o.tempOptionId &&
            option.tempOptionId === o.tempOptionId)
        ) {
          return { ...o, ...option }; // overwrite o's fields with option's fields
        }
        return o;
      });
      console.log(
        `handleUpdateOption() - setOptions() - updated option`,
        updatedOptions
      );
      setOptions(updatedOptions);
      setSelectedOption(null);
      preserveViewportOnUiShrinkAndFocusOption(appliedOptionKey);
      toast.success("Option Updated");
    }
    // option is new - push to existing arr
    else {
      option.tempOptionId = uniqueGuid(); // so that we discern from a new option and a saved option in the option ui
      appliedOptionKey = getOptionKey(option);
      const updatedOptions = [...options, option];
      console.log(
        `handleUpdateOption() - setOptions() - new option`,
        updatedOptions
      );
      setOptions(updatedOptions);
      setSelectedOption(null);
      preserveViewportOnUiShrinkAndFocusOption(appliedOptionKey);
      toast.success("Option Added");
    }
  };

  const handleTagChange = (str) => {
    const newTags = str.split(",").map((s) => s.trim().replace(/\s/g, ""));
    const compareArrays = (a, b) => {
      return JSON.stringify(a) === JSON.stringify(b);
    };
    // tags array has changed - update state
    if (!compareArrays(tags, newTags)) {
      setTags(newTags);
    }
  };

  const addArrayItem = (setter, array, newItem) => {
    setter([...array, newItem]);
  };

  const removeArrayItem = (setter, array, index) => {
    const updatedArray = array.filter((_, i) => i !== index);
    setter(updatedArray);
  };

  useEffect(() => {
    setCompanyOptions((prevCompanyOptions) => {
      let changed = false;
      const nextCompanyOptions = prevCompanyOptions.map((companyOptionObj) => {
        const currentCategory = `${companyOptionObj.companyCategory || ""}`.trim();
        if (!currentCategory || companyCategories.includes(currentCategory)) {
          return companyOptionObj;
        }
        changed = true;
        return {
          ...companyOptionObj,
          companyCategory: "",
        };
      });
      return changed ? nextCompanyOptions : prevCompanyOptions;
    });
  }, [companyCategories]);

  const handleAddAreaOfInterest = () => {
    const trimmedArea = `${areaOfInterestDraft || ""}`.trim();
    if (!trimmedArea) {
      return;
    }
    if (areasOfInterest.includes(trimmedArea)) {
      toast("Area of interest already exists");
      return;
    }
    setAreasOfInterest((prevAreas) => [...prevAreas, trimmedArea]);
    setAreaOfInterestDraft("");
  };

  const handleMoveAreaOfInterest = (index, direction) => {
    setAreasOfInterest((prevAreas) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prevAreas.length) {
        return prevAreas;
      }
      const nextAreas = [...prevAreas];
      const [movedArea] = nextAreas.splice(index, 1);
      nextAreas.splice(targetIndex, 0, movedArea);
      return nextAreas;
    });
  };

  const handleRemoveAreaOfInterest = (areaToRemove) => {
    if (!window.confirm(`Remove area of interest "${areaToRemove}"?`)) {
      return;
    }
    setAreasOfInterest((prevAreas) =>
      prevAreas.filter((area) => area !== areaToRemove)
    );
  };

  const handleAddCompanyCategory = () => {
    const trimmedCategory = `${companyCategoryDraft || ""}`.trim();
    if (!trimmedCategory) {
      return;
    }
    if (companyCategories.includes(trimmedCategory)) {
      toast("Category already exists");
      return;
    }
    setCompanyCategories((prevCategories) => [...prevCategories, trimmedCategory]);
    setCompanyCategoryDraft("");
  };

  const handleMoveCompanyCategory = (index, direction) => {
    setCompanyCategories((prevCategories) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prevCategories.length) {
        return prevCategories;
      }
      const nextCategories = [...prevCategories];
      const [movedCategory] = nextCategories.splice(index, 1);
      nextCategories.splice(targetIndex, 0, movedCategory);
      return nextCategories;
    });
  };

  const handleRemoveCompanyCategory = (categoryToRemove) => {
    if (!window.confirm(`Remove category "${categoryToRemove}"?`)) {
      return;
    }
    setCompanyCategories((prevCategories) =>
      prevCategories.filter((category) => category !== categoryToRemove)
    );
    setCompanyOptions((prevCompanyOptions) =>
      prevCompanyOptions.map((companyOptionObj) => {
        if (companyOptionObj.companyCategory !== categoryToRemove) {
          return companyOptionObj;
        }
        return {
          ...companyOptionObj,
          companyCategory: "",
        };
      })
    );
  };

  const updateCompanyOptionField = (index, fieldName, value) => {
    let normalizedValue = value;
    if (fieldName === "companyCategory") {
      normalizedValue = companyCategories.includes(value) ? value : "";
    } else if (fieldName === "companyTags") {
      normalizedValue = normalizeStringArray(value);
    }
    setCompanyOptions((prevCompanyOptions) =>
      prevCompanyOptions.map((companyOptionObj, companyIndex) => {
        if (companyIndex !== index) {
          return companyOptionObj;
        }
        return {
          ...companyOptionObj,
          [fieldName]: normalizedValue,
        };
      })
    );
  };

  const getCompanyTagDraftKey = (companyOptionObj, companyIndex) => {
    return `${companyOptionObj?.companyId || "company"}-${companyIndex}`;
  };

  const getCompanyTags = (companyOptionObj = {}) => {
    return normalizeStringArray(companyOptionObj.companyTags || []);
  };

  const handleCompanyTagDraftChange = (companyOptionObj, companyIndex, value) => {
    const draftKey = getCompanyTagDraftKey(companyOptionObj, companyIndex);
    setCompanyTagDrafts((prevDrafts) => ({
      ...prevDrafts,
      [draftKey]: value,
    }));
  };

  const clearCompanyTagDraft = (companyOptionObj, companyIndex) => {
    const draftKey = getCompanyTagDraftKey(companyOptionObj, companyIndex);
    setCompanyTagDrafts((prevDrafts) => {
      if (!Object.prototype.hasOwnProperty.call(prevDrafts, draftKey)) {
        return prevDrafts;
      }
      const nextDrafts = { ...prevDrafts };
      delete nextDrafts[draftKey];
      return nextDrafts;
    });
  };

  const handleAddCompanyTag = (companyOptionObj, companyIndex, rawTag) => {
    const trimmedTag = `${rawTag || ""}`.trim();
    if (!trimmedTag) {
      return;
    }
    const currentCompanyOption = companyOptions[companyIndex] || companyOptionObj;
    const existingTags = getCompanyTags(currentCompanyOption);
    const nextTags = normalizeStringArray([...existingTags, trimmedTag]);
    updateCompanyOptionField(companyIndex, "companyTags", nextTags);
    clearCompanyTagDraft(companyOptionObj, companyIndex);
  };

  const handleRemoveCompanyTag = (companyIndex, tagToRemove) => {
    const currentCompanyOption = companyOptions[companyIndex] || {};
    const nextTags = getCompanyTags(currentCompanyOption).filter(
      (companyTag) => companyTag !== tagToRemove
    );
    updateCompanyOptionField(companyIndex, "companyTags", nextTags);
  };

  const handleCompanyTagInputKeyDown = (event, companyOptionObj, companyIndex) => {
    const draftKey = getCompanyTagDraftKey(companyOptionObj, companyIndex);
    const draftValue = `${companyTagDrafts[draftKey] || ""}`;
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddCompanyTag(companyOptionObj, companyIndex, draftValue.trim());
      return;
    }
    if (event.key === "Backspace" && !draftValue.trim()) {
      const currentTags = getCompanyTags(companyOptions[companyIndex] || {});
      if (currentTags.length) {
        handleRemoveCompanyTag(companyIndex, currentTags[currentTags.length - 1]);
      }
    }
  };

  const handleRemoveCompanyOption = (index) => {
    if (!window.confirm("Are you sure you want to remove this company?")) {
      return;
    }
    const companyOptionObj = companyOptions[index];
    clearCompanyTagDraft(companyOptionObj, index);
    removeArrayItem(setCompanyOptions, companyOptions, index);
  };

  const uploadCompanyLogoFile = async (file, companyIndex) => {
    if (!file) return;
    if (!file.type || !file.type.startsWith("image/")) {
      toast.error("Please drop or select an image file");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append("image", file);
    formData.append("options", JSON.stringify({ compress: true }));

    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/upload/image", formData);
      if (response.success && response.imageUrl) {
        updateCompanyOptionField(companyIndex, "companyLogoUrl", response.imageUrl);
        toast.success("Company logo uploaded");
      } else {
        toast.error("Failed to upload company logo");
      }
    } catch (error) {
      console.error("Error uploading company logo:", error);
      toast.error("Error uploading company logo");
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyLogoUpload = async (e, companyIndex) => {
    const file = e.target.files[0];
    await uploadCompanyLogoFile(file, companyIndex);
    e.target.value = "";
  };

  const handleCompanyLogoDrop = async (e, companyIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCompanyRowIndex(null);
    const file = e.dataTransfer?.files?.[0];
    await uploadCompanyLogoFile(file, companyIndex);
  };

  const handleAddExistingCompanyOption = (existingCompany) => {
    const normalizedCompany = normalizeCompanyOptionForCompare(existingCompany);
    if (
      normalizedCompany.companyCategory &&
      !companyCategories.includes(normalizedCompany.companyCategory)
    ) {
      setCompanyCategories((prevCategories) => [
        ...prevCategories,
        normalizedCompany.companyCategory,
      ]);
    }
    setCompanyOptions((prevCompanyOptions) => [
      ...prevCompanyOptions,
      {
        ...normalizedCompany,
        companyId: normalizedCompany.companyId || uniqueGuid().slice(0, 6),
      },
    ]);
  };

  const getOptionKey = (optionObj) => {
    if (!optionObj) {
      return null;
    }
    return optionObj.optionId || optionObj.tempOptionId || null;
  };

  const optionMatches = (a, b) => {
    if (!a || !b) {
      return false;
    }
    if (a.optionId && b.optionId) {
      return a.optionId === b.optionId;
    }
    if (a.tempOptionId && b.tempOptionId) {
      return a.tempOptionId === b.tempOptionId;
    }
    return a === b;
  };

  const preserveViewportOnUiShrinkAndFocusOption = (optionKeyToFocus) => {
    const prevDocHeight = document.documentElement.scrollHeight;
    const prevBottomOffset =
      prevDocHeight - (window.scrollY + window.innerHeight);
    requestAnimationFrame(() => {
      const newDocHeight = document.documentElement.scrollHeight;
      const maxScrollY = Math.max(0, newDocHeight - window.innerHeight);
      const targetScrollY = Math.max(
        0,
        Math.min(maxScrollY, newDocHeight - window.innerHeight - prevBottomOffset)
      );
      window.scrollTo({ top: targetScrollY, behavior: "auto" });
      if (optionKeyToFocus && optionItemRefs.current[optionKeyToFocus]) {
        optionItemRefs.current[optionKeyToFocus].scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth",
        });
      }
    });
  };

  const handleRemoveOption = (optionToRemove, e) => {
    e.stopPropagation();
    const optionName = optionToRemove.title || "this option";
    let confirmText = `Are you sure you want to remove ${optionName}?`;
    if (selectedOption && optionMatches(selectedOption, optionToRemove) && optionDraftDirty) {
      confirmText =
        "You have unapplied edits for this option. Are you sure you want to remove it?";
    }
    if (!window.confirm(confirmText)) {
      return;
    }
    const removedOptionKey = getOptionKey(optionToRemove);
    setOptions((prevOptions) =>
      prevOptions.filter((o) => !optionMatches(o, optionToRemove))
    );
    if (selectedOption && optionMatches(selectedOption, optionToRemove)) {
      setSelectedOption(null);
      setOptionDraftDirty(false);
    }
    if (removedOptionKey && optionItemRefs.current[removedOptionKey]) {
      delete optionItemRefs.current[removedOptionKey];
    }
    toast.success("Option Removed");
  };

  const canSwitchOptionEditor = () => {
    if (!optionDraftDirty) {
      return true;
    }
    return window.confirm(
      "You have unapplied option edits. Discard them and switch options?"
    );
  };

  const newOptionMode =
    selectedOption !== null &&
    !selectedOption.optionId &&
    !selectedOption.tempOptionId
      ? true
      : false; // user is creating a new option or editing existing

  const normalizeOptionForCompare = (optionObj = {}) => {
    return {
      optionId: optionObj.optionId || null,
      title: optionObj.title || "",
      description: optionObj.description || "",
      location: optionObj.location || "",
      startTime: optionObj.startTime || null,
      endTime: optionObj.endTime || null,
      shortTitle: optionObj.shortTitle || "",
      subLocation: optionObj.subLocation || "",
      timeString: optionObj.timeString || "",
      dateString: optionObj.dateString || "",
      timezone: optionObj.timezone || timezoneOptions[0],
      logos: optionObj.logos || [],
      hideOnPage: !!optionObj.hideOnPage,
      showPresentedByLabel: !!optionObj.showPresentedByLabel,
      hidden: !!optionObj.hidden,
      emailHtml: optionObj.emailHtml || "",
    };
  };

  const eventIsDirty = useMemo(() => {
    const baseline = {
      title: selectedEvent ? selectedEvent.title || "" : "",
      startDate: selectedEvent ? selectedEvent.startDate || "" : "",
      endDate: selectedEvent ? selectedEvent.endDate || "" : "",
      location: selectedEvent ? selectedEvent.location || "" : "",
      imageUrl: selectedEvent ? selectedEvent.imageUrl || "" : "",
      signupEmailsEnabled: selectedEvent
        ? !!selectedEvent.signupEmailsEnabled
        : false,
      tags: selectedEvent ? selectedEvent.tags || [eventsTag] : [eventsTag],
      companyCategories: selectedEvent
        ? normalizeStringArray(selectedEvent.companyCategories || [])
        : [],
      options: selectedEvent
        ? (selectedEvent.options || []).map(normalizeOptionForCompare)
        : [],
      companyOptions: selectedEvent
        ? (selectedEvent.companyOptions || []).map(normalizeCompanyOptionForCompare)
        : [],
      bdOptions: bdOptionsFromEvent(selectedEvent),
    };

    const current = {
      title: title || "",
      startDate: startDate || "",
      endDate: endDate || "",
      location: location || "",
      imageUrl: imageUrl || "",
      signupEmailsEnabled: !!signupEmailsEnabled,
      tags: tags || [eventsTag],
      companyCategories: normalizeStringArray(companyCategories || []),
      options: (options || []).map(normalizeOptionForCompare),
      companyOptions: (companyOptions || []).map(normalizeCompanyOptionForCompare),
      bdOptions,
    };

    return JSON.stringify(baseline) !== JSON.stringify(current);
  }, [
    selectedEvent,
    title,
    startDate,
    endDate,
    location,
    imageUrl,
    signupEmailsEnabled,
    tags,
    companyCategories,
    options,
    companyOptions,
    bdOptions,
    eventsTag,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!eventIsDirty) {
        return;
      }
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [eventIsDirty]);

  const saveBtnText = "Save Event to Database";

  const isShortDateValid = (val) => /^[A-Za-z]+\s\d{1,2}$/.test(val);

  return (
    <>
      <style>
        {`
          .portfolio-company-modal .modal-dialog {
            max-width: calc(100vw - 2.5rem);
            margin: 1.25rem auto;
          }
          .portfolio-company-modal .modal-content {
            border-radius: 12px;
            border: 1px solid #d4e0ff;
            background: #f8fbff;
          }
          .portfolio-company-modal .modal-header {
            background: linear-gradient(180deg, #edf3ff 0%, #e3eeff 100%);
            border-bottom: 1px solid #c9dafd;
          }
          .portfolio-company-modal .modal-body {
            overflow-x: auto;
          }
          .portfolio-company-modal .modal-body .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            /* keep the horizontal scrollbar reachable without scrolling to
               the bottom of a long companies list */
            max-height: 60vh;
            overflow-y: auto;
          }
          .portfolio-company-modal .portfolio-company-table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
          }
          .portfolio-company-table thead th {
            background: #dce9ff;
            border-color: #bfd2fb;
            color: #194091;
            font-weight: 700;
            font-size: 12px;
            padding: 8px;
            vertical-align: middle;
          }
          .portfolio-company-table tbody td {
            padding: 6px;
            border-color: #d6e3ff;
            background: #fdfefe;
            vertical-align: middle;
          }
          .portfolio-company-table tbody tr:hover td {
            background: #f1f7ff;
          }
          .portfolio-company-table tbody tr.company-row-active td {
            background: #e7f1ff;
          }
          .portfolio-company-input {
            min-height: 30px;
            padding: 4px 8px;
            font-size: 12px;
            border-color: #b9ccf0;
          }
          .portfolio-company-input:focus {
            border-color: #6394ef;
            box-shadow: 0 0 0 0.12rem rgba(44, 115, 255, 0.25);
          }
          .company-logo-dropzone {
            min-height: 30px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            width: 100%;
            border: 1px dashed #8caeea;
            background: #eef4ff4d;
            color: #1f52b6;
            border-radius: 6px;
            padding: 5px 8px;
            font-size: 12px;
            cursor: pointer;
            margin-bottom: 4px;
          }
          .company-logo-dropzone:hover {
            background: #e5efff;
            border-color: #6c95e4;
          }
          .company-logo-dropzone.drag-over {
            background: #dceaff;
            border-color: #3f73d8;
            color: #1749ac;
          }
          .icon-danger-btn {
            border: 0;
            background: transparent;
            color: #c23232;
            font-weight: 700;
            font-size: 15px;
            line-height: 1;
            padding: 2px 6px;
            border-radius: 5px;
          }
          .icon-danger-btn:hover,
          .icon-danger-btn:focus {
            background: rgba(194, 50, 50, 0.14);
            color: #a62828;
          }
          .primary-btn {
            background-color: #2b67db;
            border-color: #2b67db;
            color: #fff;
          }
          .primary-btn:hover {
            background-color: #1e55c1;
            border-color: #1e55c1;
            color: #fff;
          }
          .company-category-badges {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }
          .company-category-manager {
            max-width: 760px;
            background: #edf3ff;
            border: 1px solid #c9dafd;
            border-radius: 10px;
            padding: 12px;
          }
          .section-usage-note {
            display: table;
            margin-bottom: 10px;
            padding: 3px 12px;
            border-radius: 999px;
            background: #dce9ff;
            color: #194091;
            font-size: 12px;
            font-weight: 600;
          }
          .company-category-input-row {
            max-width: 760px;
            display: flex;
            align-items: stretch;
            flex-wrap: nowrap;
            gap: 10px;
            margin-bottom: 8px;
          }
          .company-category-input {
            flex: 1 1 auto;
            min-width: 0;
            height: 38px;
          }
          .company-category-add-btn {
            flex: 0 0 auto;
            min-width: 140px;
            height: 38px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            white-space: nowrap;
            padding: 0 14px;
          }
          .company-category-pill {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            border-radius: 999px;
            padding: 4px 10px;
            background: #dce9ff;
            color: #194091;
            font-size: 12px;
            font-weight: 600;
          }
          .company-category-pill button {
            border: 0;
            background: transparent;
            color: #194091;
            padding: 0;
            line-height: 1;
            font-size: 11px;
            font-weight: 700;
          }
          .company-category-pill .company-category-move-btn {
            font-size: 14px;
          }
          .company-category-pill .company-category-move-btn:disabled {
            opacity: 0.3;
            cursor: default;
          }
          .company-tags-editor {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 6px;
            min-height: 30px;
            padding: 4px 8px;
            border: 1px solid #b9ccf0;
            border-radius: 0.25rem;
            background: #fff;
          }
          .company-tags-editor:focus-within {
            border-color: #6394ef;
            box-shadow: 0 0 0 0.12rem rgba(44, 115, 255, 0.25);
          }
          .company-tag-pill {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            border-radius: 999px;
            padding: 2px 8px;
            background: #dce9ff;
            color: #194091;
            font-size: 11px;
            font-weight: 600;
            line-height: 1.2;
          }
          .company-tag-pill-remove {
            border: 0;
            background: transparent;
            color: #194091;
            padding: 0;
            font-size: 11px;
            font-weight: 700;
            line-height: 1;
          }
          .company-tags-editor-input {
            flex: 1 0 110px;
            min-width: 90px;
            border: 0;
            outline: none;
            padding: 0;
            margin: 0;
            height: 22px;
            font-size: 12px;
            background: transparent;
          }
        `}
      </style>
      <div className="event-form">
      <div className="row">
        <div className="form-group mb-3 col-md-6">
          <label className="form-label">
            Title <span className="example-text">(e.g. Blackhat)</span>
          </label>
          <input
            type="text"
            value={title}
            className="form-control"
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="form-group mb-3 col-md-6">
          <label className="form-label">
            Location <span className="example-text">(e.g. Las Vegas, NV)</span>
          </label>
          <input
            type="text"
            value={location}
            className="form-control"
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>
        <div className="form-group mb-3 col-md-6">
          <label className="form-label">
            Start Date <span className="example-text">(e.g. August 10)</span>
          </label>
          <input
            type="text"
            value={startDate}
            className="form-control"
            onChange={(e) => setStartDate(e.target.value)}
          />
          {!isShortDateValid(startDate) && startDate && (
            <small className="text-danger">
              Please use the format: August 10
            </small>
          )}
        </div>
        <div className="form-group mb-3 col-md-6">
          <label className="form-label">
            End Date <span className="example-text">(e.g. August 14)</span>
          </label>
          <input
            type="text"
            value={endDate}
            className="form-control"
            onChange={(e) => setEndDate(e.target.value)}
          />
          {!isShortDateValid(endDate) && endDate && (
            <small className="text-danger">
              Please use the format: August 14
            </small>
          )}
        </div>
        <div className="form-group mb-3 col-md-12">
          <label className="form-label">
            Logo <span className="example-text">(for internal use only)</span>
          </label>
          <div className="existing-logos-container">
            <label className="form-label">
              Click to add a previously used logo:
            </label>
            <div className="existing-logos-outer">
              {allExistingEventLogos.map((existingLogoSrc, index) => {
                return (
                  <div
                    key={`${existingLogoSrc}-${index}`}
                    onClick={() => setImageUrl(existingLogoSrc)}
                    className="existing-logo"
                    style={{ maxWidth: "60px" }}
                  >
                    <img
                      src={existingLogoSrc}
                      alt={`Existing Event Logo ${index + 1}`}
                      className="img-fluid"
                      style={{ maxWidth: "60px" }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {imageUrl ? (
            <div className="d-flex align-items-center mb-2">
              <img
                src={imageUrl}
                alt={`Event Logo`}
                className="img-thumbnail"
                style={{ maxWidth: "120px" }}
              />
              <button
                className="btn btn-danger ml-2"
                onClick={() => setImageUrl("")}
              >
                Remove
              </button>
            </div>
          ) : null}
          {loading && (
            <img
              src={imageUploadLoader}
              alt="Uploading..."
              width="100"
              className="img-thumbnail"
            />
          )}

          {!imageUrl ? (
            <>
              {" "}
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setImageUrl, {
                    compress: true,
                  })
                }
                className="d-none"
                id="eventLogoUpload"
              />
              <button
                className="btn btn-secondary btn-sm mt-2"
                onClick={() =>
                  document.getElementById("eventLogoUpload").click()
                }
              >
                Upload Logo
                <i className="event-owner-add fa-sharp fa-solid fa-plus ml-05"></i>
              </button>
            </>
          ) : null}

          {/* <input
            type="text"
            value={imageUrl}
            className="form-control"
            onChange={(e) => setImageUrl(e.target.value)}
          /> */}
        </div>
        <div className="form-group mb-3 col-md-12">
          <div className="form-check">
            <input
              id="enableSignupEmails"
              type="checkbox"
              className="form-check-input"
              checked={signupEmailsEnabled}
              onChange={(e) => setSignupEmailsEnabled(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="enableSignupEmails">
              Enable signup emails{" "}
              <span className="example-text">
                (send the signup email when a new RSVP is submitted)
              </span>
            </label>
          </div>
        </div>
        {!isBdMode ? (
        <div className="form-group mb-3 col-md-12">
          <div className="options-wrapper">
            <span className="section-usage-note">
              Used for C2Ps such as UNGA, CES, etc.
            </span>
            <label className="form-label">
              Event options which the attendee can request to attend<br></br>
              <span className="example-text">
                (e.g. VIP Brunch or Cocktail Reception)
              </span>
            </label>
            <label className="form-label">
              Click on an existing option to edit it or add a new option.
            </label>
            <div className="edit-options-container">
              {options.map((o, index) => {
                const selClass = o === selectedOption ? "selected" : "";
                const optionKey = getOptionKey(o) || `option-${index}`;
                return (
                  <div
                    key={optionKey}
                    ref={(el) => {
                      if (el) {
                        optionItemRefs.current[optionKey] = el;
                      } else {
                        delete optionItemRefs.current[optionKey];
                      }
                    }}
                    className={`option-container ${selClass}`}
                    onClick={() => {
                      if (!canSwitchOptionEditor()) {
                        return;
                      }
                      console.log("selectedOption", o);
                      setSelectedOption(o);
                    }}
                  >
                    <button
                      type="button"
                      className="remove-option-btn"
                      onClick={(e) => handleRemoveOption(o, e)}
                      title="Remove option"
                      aria-label="Remove option"
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                    <div className="title">{o.title}</div>
                    <div className="location">{o.description}</div>
                  </div>
                );
              })}
              <div
                className={`option-container new text-center ${
                  newOptionMode ? "selected" : ""
                }`}
                onClick={() => {
                  if (!canSwitchOptionEditor()) {
                    return;
                  }
                  console.log("selectedOption", {});
                  setSelectedOption({});
                }}
              >
                <div className="title text-center">
                  New Option{" "}
                  <i className="event-owner-add fa-sharp fa-solid fa-plus ml-05"></i>
                </div>
              </div>
            </div>
            <AnimatePresence initial={false}>
              {selectedOption !== null ? (
                <motion.div
                  key={getOptionKey(selectedOption) || "new-option-form"}
                  className="option-form-anim-wrapper"
                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                  animate={{ opacity: 1, height: "auto", marginTop: 8 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <OptionForm
                    option={selectedOption}
                    options={options}
                    employee={employee}
                    handleUpdateOption={handleUpdateOption}
                    onDraftDirtyChange={setOptionDraftDirty}
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
        ) : null}
        {!isBdMode ? (
        <div className="form-group mb-3 col-md-12">
          <div className="options-wrapper">
            <span className="section-usage-note">
              Used for events such as SMART Summit, FOAI Summit, etc.
            </span>
            <label className="form-label">
              Areas of Interest<br></br>
              <span className="example-text">
                Options shown as checkboxes on the public RSVP form.
              </span>
            </label>
            <div className="company-category-input-row">
              <input
                type="text"
                className="form-control company-category-input"
                placeholder="Add area of interest (e.g. Enterprise AI)"
                value={areaOfInterestDraft}
                onChange={(e) => setAreaOfInterestDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddAreaOfInterest();
                  }
                }}
              />
              <button
                type="button"
                className="btn primary-btn company-category-add-btn"
                onClick={handleAddAreaOfInterest}
              >
                Add Area
              </button>
            </div>
            {areasOfInterest.length ? (
              <>
                <div className="company-category-badges">
                  {areasOfInterest.map((area, areaIndex) => (
                    <div key={area} className="company-category-pill">
                      <button
                        type="button"
                        className="company-category-move-btn"
                        onClick={() => handleMoveAreaOfInterest(areaIndex, -1)}
                        disabled={areaIndex === 0}
                        aria-label={`Move ${area} earlier`}
                        title="Move earlier"
                      >
                        ‹
                      </button>
                      <span>{area}</span>
                      <button
                        type="button"
                        className="company-category-move-btn"
                        onClick={() => handleMoveAreaOfInterest(areaIndex, 1)}
                        disabled={areaIndex === areasOfInterest.length - 1}
                        aria-label={`Move ${area} later`}
                        title="Move later"
                      >
                        ›
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveAreaOfInterest(area)}
                        aria-label={`Remove ${area}`}
                        title={`Remove ${area}`}
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
                <span className="example-text">
                  Areas render on the RSVP form in this order. Use the arrows to
                  reorder, then save the event.
                </span>
              </>
            ) : (
              <span className="example-text">
                No areas of interest yet. The RSVP form hides the section when
                the list is empty.
              </span>
            )}
          </div>
        </div>
        ) : null}
        {isBdMode ? (
          <div className="form-group mb-3 col-md-12">
            <div className="options-wrapper">
              <span className="section-usage-note">
                BD Portfolio page content
              </span>
              <label className="form-label">
                Page Copy<br></br>
                <span className="example-text">
                  Copy shown on the public BD portfolio page. Leave a field
                  empty to use the page default.
                </span>
              </label>
              <div className="row">
                <div className="form-group mb-3 col-md-12">
                  <label className="form-label">
                    Hero Title{" "}
                    <span className="example-text">
                      (e.g. Landmark Cybersecurity Portfolio)
                    </span>
                  </label>
                  <AutoGrowTextarea
                    value={bdOptions.bdHeroTitle}
                    onChange={updateBdOption("bdHeroTitle")}
                  />
                </div>
                <div className="form-group mb-3 col-md-12">
                  <label className="form-label">
                    Hero Subheading{" "}
                    <span className="example-text">(e.g. Landmark Ventures)</span>
                  </label>
                  <AutoGrowTextarea
                    value={bdOptions.bdHeroSubheading}
                    onChange={updateBdOption("bdHeroSubheading")}
                  />
                </div>
                <div className="form-group mb-3 col-md-12">
                  <label className="form-label">
                    Hero Body / Portfolio Description{" "}
                    <span className="example-text">
                      (shown under the hero title — new lines or \n become
                      line breaks on the page)
                    </span>
                  </label>
                  <AutoGrowTextarea
                    value={bdOptions.bdHeroBody}
                    onChange={updateBdOption("bdHeroBody")}
                  />
                </div>
                <div className="form-group mb-3 col-md-12">
                  <label className="form-label">
                    Hero Button Text{" "}
                    <span className="example-text">
                      (e.g. Request 1:1 portfolio meetings)
                    </span>
                  </label>
                  <AutoGrowTextarea
                    value={bdOptions.bdHeroButtonText}
                    onChange={updateBdOption("bdHeroButtonText")}
                  />
                </div>
                <div className="form-group mb-3 col-md-12">
                  <label className="form-label">
                    Follow-up Contact Name{" "}
                    <span className="example-text">
                      (shown above the submit button; hidden if empty)
                    </span>
                  </label>
                  <AutoGrowTextarea
                    value={bdOptions.bdContactName}
                    onChange={updateBdOption("bdContactName")}
                  />
                </div>
                <div className="form-group mb-3 col-md-12">
                  <label className="form-label">Follow-up Contact Email</label>
                  <AutoGrowTextarea
                    value={bdOptions.bdContactEmail}
                    onChange={updateBdOption("bdContactEmail")}
                  />
                </div>
                <div className="form-group mb-3 col-md-12">
                  <label className="form-label">
                    Success Message{" "}
                    <span className="example-text">
                      (shown after the form is submitted — new lines or \n
                      become line breaks on the page)
                    </span>
                  </label>
                  <AutoGrowTextarea
                    value={bdOptions.bdSuccessMessage}
                    onChange={updateBdOption("bdSuccessMessage")}
                  />
                </div>
              </div>
              <label className="form-label">
                Page Metadata<br></br>
                <span className="example-text">
                  SEO and social share (Twitter/LinkedIn/Slack card) tags baked
                  into the page HTML. Changes require a redeploy of the page on
                  Vercel to take effect.
                </span>
              </label>
              <div className="row">
                <div className="form-group mb-3 col-md-12">
                  <label className="form-label">
                    Meta Title{" "}
                    <span className="example-text">
                      (browser tab + share card title; falls back to Hero Title)
                    </span>
                  </label>
                  <AutoGrowTextarea
                    value={bdOptions.bdMetaTitle}
                    onChange={updateBdOption("bdMetaTitle")}
                  />
                </div>
                <div className="form-group mb-3 col-md-12">
                  <label className="form-label">
                    Meta Description{" "}
                    <span className="example-text">
                      (share card description; falls back to Hero Body)
                    </span>
                  </label>
                  <AutoGrowTextarea
                    value={bdOptions.bdMetaDescription}
                    onChange={updateBdOption("bdMetaDescription")}
                  />
                </div>
                <div className="form-group mb-3 col-md-12">
                  <label className="form-label">
                    Page URL{" "}
                    <span className="example-text">
                      (canonical url, e.g. https://cyber.landmarkventures.com)
                    </span>
                  </label>
                  <AutoGrowTextarea
                    value={bdOptions.bdMetaUrl}
                    onChange={updateBdOption("bdMetaUrl")}
                  />
                </div>
                <div className="form-group mb-3 col-md-12">
                  <label className="form-label">
                    Share Image{" "}
                    <span className="example-text">
                      (og:image / twitter:image — shown on share cards, ideally
                      1200x630. Upload an image or paste a URL.)
                    </span>
                  </label>
                  {bdOptions.bdMetaImageUrl ? (
                    <div className="d-flex align-items-center mb-2">
                      <img
                        src={bdOptions.bdMetaImageUrl}
                        alt="Share card preview"
                        className="img-thumbnail"
                        style={{ maxWidth: "160px" }}
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm ml-2"
                        onClick={() =>
                          setBdOptions((prev) => ({
                            ...prev,
                            bdMetaImageUrl: "",
                          }))
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ) : null}
                  {loading && (
                    <img
                      src={imageUploadLoader}
                      alt="Uploading..."
                      width="100"
                      className="img-thumbnail"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleImageUpload(
                        e,
                        (url) =>
                          setBdOptions((prev) => ({
                            ...prev,
                            bdMetaImageUrl: url,
                          })),
                        { compress: true }
                      )
                    }
                    className="d-none"
                    id="bdMetaImageUpload"
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm mb-2"
                    onClick={() =>
                      document.getElementById("bdMetaImageUpload").click()
                    }
                  >
                    Upload Share Image
                    <i className="event-owner-add fa-sharp fa-solid fa-plus ml-05"></i>
                  </button>
                  <AutoGrowTextarea
                    value={bdOptions.bdMetaImageUrl}
                    onChange={updateBdOption("bdMetaImageUrl")}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <div className="form-group mb-3 col-md-12">
          <div className="options-wrapper">
            <span className="section-usage-note">
              {isBdMode
                ? "Companies shown on the BD portfolio page."
                : "Used for C2Ps such as BlackHat."}
            </span>
            <label className="form-label">
              Portfolio Companies<br></br>
              <span className="example-text">
                Add/edit companies used for meeting booking on the event page.
              </span>
            </label>
            <button
              type="button"
              className="btn btn-sm primary-btn mr-2"
              onClick={() => setShowCompanyModal(true)}
            >
              Manage Portfolio Companies ({companyOptions.length})
              <i className="event-owner-add fa-sharp fa-solid fa-pen-to-square ml-05"></i>
            </button>
            <div className="mt-2">
              {companyOptions.length ? (
                <span className="example-text">
                  {companyOptions.length} companies selected:{" "}
                  {companyOptions
                    .slice(0, 5)
                    .map((companyOptionObj) => companyOptionObj.companyName || "Untitled")
                    .join(", ")}
                  {companyOptions.length > 5 ? ", ..." : ""}
                </span>
              ) : (
                <span className="example-text">No companies added yet.</span>
              )}
            </div>
            <div className="mt-1">
              {companyCategories.length ? (
                <span className="example-text">
                  {companyCategories.length} categories:{" "}
                  {companyCategories.slice(0, 8).join(", ")}
                  {companyCategories.length > 8 ? ", ..." : ""}
                </span>
              ) : (
                <span className="example-text">No company categories created yet.</span>
              )}
            </div>
          </div>
        </div>
        <Modal
          show={showCompanyModal}
          onHide={() => setShowCompanyModal(false)}
          className="portfolio-company-modal"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>Portfolio Companies</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3 company-category-manager">
              <label className="form-label">Company Categories</label>
              <div className="company-category-input-row">
                <input
                  type="text"
                  className="form-control company-category-input"
                  placeholder="Add category (e.g. AI Security)"
                  value={companyCategoryDraft}
                  onChange={(e) => setCompanyCategoryDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCompanyCategory();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn primary-btn company-category-add-btn"
                  onClick={handleAddCompanyCategory}
                >
                  Add Category
                </button>
              </div>
              {companyCategories.length ? (
                <>
                  <div className="company-category-badges">
                    {companyCategories.map((category, categoryIndex) => (
                      <div key={category} className="company-category-pill">
                        <button
                          type="button"
                          className="company-category-move-btn"
                          onClick={() => handleMoveCompanyCategory(categoryIndex, -1)}
                          disabled={categoryIndex === 0}
                          aria-label={`Move ${category} earlier`}
                          title="Move earlier"
                        >
                          ‹
                        </button>
                        <span>{category}</span>
                        <button
                          type="button"
                          className="company-category-move-btn"
                          onClick={() => handleMoveCompanyCategory(categoryIndex, 1)}
                          disabled={categoryIndex === companyCategories.length - 1}
                          aria-label={`Move ${category} later`}
                          title="Move later"
                        >
                          ›
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveCompanyCategory(category)}
                          aria-label={`Remove ${category}`}
                          title={`Remove ${category}`}
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                  <span className="example-text">
                    Categories render on the event page in this order. Use the
                    arrows to reorder, then save the event.
                  </span>
                </>
              ) : (
                <span className="example-text">
                  No categories yet. Add categories here, then assign them to each company.
                </span>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Add previously used companies</label>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  maxHeight: "180px",
                  overflowY: "auto",
                }}
              >
                {allExistingCompanyOptions.length ? (
                  allExistingCompanyOptions.map((existingCompany, index) => {
                    return (
                      <button
                        key={`${existingCompany.companyId}-${index}`}
                        type="button"
                        className="btn btn-sm primary-btn text-start"
                        style={{ minWidth: "220px" }}
                        onClick={() => handleAddExistingCompanyOption(existingCompany)}
                      >
                        <div style={{ fontWeight: 600 }}>
                          {existingCompany.companyName || "Untitled Company"}
                        </div>
                        <div style={{ fontSize: "12px", opacity: 0.85 }}>
                          {existingCompany.companyCategory || "No category"}
                        </div>
                        <div style={{ fontSize: "12px", opacity: 0.85 }}>
                          {existingCompany.companyWebsite || "No website"}
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <span className="example-text">
                    No unused historical companies found.
                  </span>
                )}
              </div>
            </div>
            <Table responsive bordered hover size="sm" className="portfolio-company-table">
              <thead>
                <tr>
                  <th style={{ minWidth: "170px" }}>Company</th>
                  <th style={{ minWidth: "170px" }}>Category</th>
                  <th style={{ minWidth: "260px" }}>Tags (press Enter)</th>
                  <th style={{ minWidth: "180px" }}>Website</th>
                  <th style={{ minWidth: "220px" }}>Summary</th>
                  <th style={{ minWidth: "280px" }}>Description</th>
                  <th style={{ minWidth: "180px" }}>Logo</th>
                  <th style={{ minWidth: "110px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(companyOptions || []).map((companyOptionObj, companyIndex) => {
                  const logoUploadInputId = `companyLogoUpload-${companyIndex}`;
                  return (
                    <tr
                      key={`${companyOptionObj.companyId || "company"}-${companyIndex}`}
                      className={activeCompanyRowIndex === companyIndex ? "company-row-active" : ""}
                      onFocusCapture={() => setActiveCompanyRowIndex(companyIndex)}
                      onBlurCapture={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                          setActiveCompanyRowIndex((prevIdx) =>
                            prevIdx === companyIndex ? null : prevIdx
                          );
                        }
                      }}
                    >
                      <td>
                        <input
                          type="text"
                          value={companyOptionObj.companyName || ""}
                          className="form-control form-control-sm portfolio-company-input"
                          onChange={(e) =>
                            updateCompanyOptionField(
                              companyIndex,
                              "companyName",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <select
                          value={companyOptionObj.companyCategory || ""}
                          className="form-control form-control-sm portfolio-company-input"
                          onChange={(e) =>
                            updateCompanyOptionField(
                              companyIndex,
                              "companyCategory",
                              e.target.value
                            )
                          }
                        >
                          <option value="">No category</option>
                          {companyCategories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div className="company-tags-editor">
                          {getCompanyTags(companyOptionObj).map((companyTag) => (
                            <span
                              key={`${companyOptionObj.companyId || "company"}-${companyTag}`}
                              className="company-tag-pill"
                            >
                              <span>{companyTag}</span>
                              <button
                                type="button"
                                className="company-tag-pill-remove"
                                aria-label={`Remove tag ${companyTag}`}
                                title={`Remove tag ${companyTag}`}
                                onClick={() =>
                                  handleRemoveCompanyTag(companyIndex, companyTag)
                                }
                              >
                                x
                              </button>
                            </span>
                          ))}
                          <input
                            type="text"
                            value={
                              companyTagDrafts[
                                getCompanyTagDraftKey(companyOptionObj, companyIndex)
                              ] || ""
                            }
                            className="company-tags-editor-input"
                            placeholder="Type tag + Enter"
                            onChange={(e) =>
                              handleCompanyTagDraftChange(
                                companyOptionObj,
                                companyIndex,
                                e.target.value
                              )
                            }
                            onKeyDown={(e) =>
                              handleCompanyTagInputKeyDown(
                                e,
                                companyOptionObj,
                                companyIndex
                              )
                            }
                          />
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={companyOptionObj.companyWebsite || ""}
                          className="form-control form-control-sm portfolio-company-input"
                          onChange={(e) =>
                            updateCompanyOptionField(
                              companyIndex,
                              "companyWebsite",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={companyOptionObj.companySummary || ""}
                          className="form-control form-control-sm portfolio-company-input"
                          onChange={(e) =>
                            updateCompanyOptionField(
                              companyIndex,
                              "companySummary",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <textarea
                          rows="1"
                          value={companyOptionObj.companyDescription || ""}
                          className="form-control form-control-sm portfolio-company-input"
                          style={{ minHeight: "30px", maxHeight: "72px", resize: "vertical" }}
                          onChange={(e) =>
                            updateCompanyOptionField(
                              companyIndex,
                              "companyDescription",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        {companyOptionObj.companyLogoUrl ? (
                          <div className="mb-1 d-flex align-items-start">
                            <img
                              src={companyOptionObj.companyLogoUrl}
                              alt={`${companyOptionObj.companyName || "Company"} logo`}
                              className="img-thumbnail"
                              style={{ maxWidth: "120px", maxHeight: "64px" }}
                            />
                            <button
                              type="button"
                              className="icon-danger-btn ml-1"
                              onClick={() =>
                                updateCompanyOptionField(companyIndex, "companyLogoUrl", "")
                              }
                              title="Remove logo"
                              aria-label="Remove logo"
                            >
                              X
                            </button>
                          </div>
                        ) : null}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleCompanyLogoUpload(e, companyIndex)}
                          className="d-none"
                          id={logoUploadInputId}
                        />
                        {!companyOptionObj.companyLogoUrl ? (
                          <label
                            htmlFor={logoUploadInputId}
                            className={`company-logo-dropzone ${
                              dragOverCompanyRowIndex === companyIndex ? "drag-over" : ""
                            }`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDragOverCompanyRowIndex(companyIndex);
                            }}
                            onDragEnter={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDragOverCompanyRowIndex(companyIndex);
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (
                                !e.currentTarget.contains(e.relatedTarget)
                              ) {
                                setDragOverCompanyRowIndex((prevIdx) =>
                                  prevIdx === companyIndex ? null : prevIdx
                                );
                              }
                            }}
                            onDrop={(e) => handleCompanyLogoDrop(e, companyIndex)}
                          >
                            Drag and drop logo here (or click)
                          </label>
                        ) : null}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="icon-danger-btn"
                          onClick={() => handleRemoveCompanyOption(companyIndex)}
                          title="Remove company"
                          aria-label="Remove company"
                        >
                          X
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!companyOptions.length ? (
                  <tr>
                    <td colSpan={8}>
                      <span className="example-text">
                        No companies added yet. Add one below or from previously used list.
                      </span>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-sm primary-btn"
              onClick={() =>
                addArrayItem(setCompanyOptions, companyOptions, createNewCompanyOption())
              }
            >
              Add New Company
              <i className="event-owner-add fa-sharp fa-solid fa-plus ml-05"></i>
            </button>
            <button
              type="button"
              className="btn btn-sm primary-btn"
              onClick={() => setShowCompanyModal(false)}
            >
              Done
            </button>
          </Modal.Footer>
        </Modal>
        {/* <div className="form-group mb-3 col-md-12">
          <label className="form-label">
            Tags determine which page(s) your Event is displayed on. For example
            a tag of <b>industrialtech</b> will display the event on{" "}
            <a
              href="https://industrialtech.landmarkventures.com/"
              target="_blank"
            >
              industrialtech.landmarkventures.com
            </a>
            . If your event has it's own dedicated website then use the tag{" "}
            <b>global</b>
            <br></br>
            <span className="example-text">
              Multiple tags should be separated by commas (e.g.
              global,industrialtech)
            </span>
          </label>
          <input
            type="text"
            value={tags.join(",")}
            className="form-control"
            onChange={(e) => handleTagChange(e.target.value)}
          />
        </div> */}
      </div>
      {optionDraftDirty ? (
        <div className="option-draft-warning mb-2">
          You have unapplied option edits. Click "Apply Option Changes" first.
        </div>
      ) : null}
      {eventIsDirty ? (
        <div className="option-draft-warning mb-2">
          You have unsaved event changes.
        </div>
      ) : null}
      <button
        className={`create-event-btn btn btn-sm btn-primary mb-3`}
        disabled={optionDraftDirty}
        onClick={handleSaveEvent}
      >
        {saveBtnText}
        <i className="event-owner-add fa-sharp fa-solid fa-check ml-05"></i>
      </button>
    </div>
    </>
  );
};

const AddEditEvent = ({ employee, onEventsUpdated, eventsTag = "global" }) => {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  async function getEvents() {
    try {
      const api = new ApiService({ employee });
      const tag = eventsTag || "global";
      const eventsResponse = await api.get(`landmark/events/getEvents/${tag}`);
      console.log(`getEvents()`, eventsResponse);
      setEvents(eventsResponse);
      setSelectedEvent(null);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    getEvents();
  }, [eventsTag]);

  const toggleSetShowForm = () => {
    setShowForm(!showForm);
  };

  const handleSetCreateMode = () => {
    setEditMode(false);
    setCreateMode(true);
    setSelectedEvent(null);
  };

  const handleSetEditMode = () => {
    setCreateMode(false);
    setEditMode(true);
  };

  const resetModes = () => {
    setCreateMode(false);
    setEditMode(false);
    setSelectedEvent(null);
  };

  return (
    <>
      <div className="add-edit-btn-container">
        {!showForm && (
          <>
            <button
              className={`create-event-btn btn btn-sm btn-primary`}
              onClick={handleSetCreateMode}
            >
              {`Create New Event`}
              <i className="event-owner-add fa-sharp fa-solid fa-plus ml-05"></i>
            </button>
            <button
              className={`create-event-btn btn btn-sm btn-primary`}
              onClick={handleSetEditMode}
            >
              {`Edit Existing Event`}
              <i className="event-owner-add fa-sharp fa-solid fa-pencil ml-05"></i>
            </button>
          </>
        )}
      </div>
      {editMode ? (
        <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
          <Card className="mb-3 h-fc bot-table">
            <div className="row">
              <div className="outputHeader">Edit Event</div>
              <>
                <div className="events-owners-subheading">
                  Select Event to Edit
                </div>
                <div
                  className="event-owners-wrapper"
                  style={{ marginBottom: "12px" }}
                >
                  {events.map((event) => {
                    const selectedClass =
                      selectedEvent && event.eventId === selectedEvent.eventId
                        ? "selected"
                        : "";
                    return (
                      <div key={event.eventId}>
                        <div
                          className={`event-owner-container selectable ${selectedClass}`}
                          onClick={() => setSelectedEvent(event)}
                        >
                          <img
                            src={event.imageUrl}
                            className="img-fluid event-img"
                          />
                          <div className="event-title">{event.title}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {selectedEvent ? (
                  <>
                    <EventForm
                      selectedEvent={selectedEvent}
                      employee={employee}
                      getEvents={getEvents}
                      resetModes={resetModes}
                      events={events}
                      onEventsUpdated={onEventsUpdated}
                      eventsTag={eventsTag}
                    />
                  </>
                ) : null}
              </>
            </div>
          </Card>
        </motion.div>
      ) : null}
      {createMode ? (
        <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
          <Card className="mb-3 h-fc bot-table">
            <div className="row">
              <div className="outputHeader">Create Event</div>
              <>
                {/* <div className="events-owners-subheading">
                </div> */}
                <>
                  <EventForm
                    selectedEvent={selectedEvent}
                    employee={employee}
                    getEvents={getEvents}
                    resetModes={resetModes}
                    events={events}
                    onEventsUpdated={onEventsUpdated}
                    eventsTag={eventsTag}
                  />
                </>
              </>
            </div>
          </Card>
        </motion.div>
      ) : null}
    </>
  );

  return (
    <>
      <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
        <Card className="mb-3 h-fc bot-table">
          <div className="row">
            <div className="outputHeader">Events</div>
            <>
              <div className="events-owners-subheading">
                Select Event to view RSVPs
              </div>
              <div
                className="event-owners-wrapper"
                style={{ marginBottom: "12px" }}
              >
                {events.map((event) => {
                  const selectedClass =
                    selectedEvent && event.eventId === selectedEvent.eventId
                      ? "selected"
                      : "";
                  return (
                    <div key={event.eventId}>
                      <div
                        className={`event-owner-container selectable ${selectedClass}`}
                        onClick={() => setSelectedEvent(event)}
                      >
                        <img
                          src={event.imageUrl}
                          className="img-fluid event-img"
                        />
                        <div className="event-title">{event.title}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          </div>
        </Card>
      </motion.div>
    </>
  );
};

export default AddEditEvent;

